import type { AxiosInstance } from 'axios';
import type {
  GetProviderConfigurationsParams,
  GetProviderDisplayInfoParams,
  ProviderConfigurationsResponse,
  ProviderDisplayInfoResponse,
  ProviderListResponse,
} from './contracts/provider';
import type { Config, ConfigListByProviderResponse, GetConfigsByProviderParams } from './contracts/config';
import type {
  GetServiceGroupsParams,
  Queue,
  QueueListResponse,
  QueueMember,
  GetMemberQueuesRequest,
  ServiceGroupsResponse,
} from './contracts/queues';
import type { MetaTemplateConfigResponse, MetaTemplateConfigsResponse } from './contracts/meta-template';
import type {
  FindOrCreateQueueRuleRequest,
  GetRootRulesRequest,
  GetRuleByIdRequest,
  GetRulesCountRequest,
  GetRulesRequest,
  RootRulesResponse,
  RuleResponse,
  RulesCountResponse,
  RulesResponse,
  QueueRule,
} from './contracts/rule';

/**
 * Gateway for interacting with the Chat Config API.
 */
export class ChatConfigGateway {
  constructor(
    private readonly httpClient: AxiosInstance,
    private readonly baseUrl: string,
  ) {}

  /**
   * Retrieves all available providers.
   * @returns The list of providers.
   */
  async getProviders(): Promise<ProviderListResponse> {
    const { data } = await this.httpClient.get<ProviderListResponse>(`${this.baseUrl}/providers`);
    return data;
  }

  async getConfigById(configId: string): Promise<Config> {
    const { data } = await this.httpClient.get<Config>(`${this.baseUrl}/config/${configId}`);
    return data;
  }

  /**
   * Retrieves provider configurations filtered by configuration IDs.
   * @param params - Query parameters containing one or more configuration IDs.
   * @returns The provider configurations matching the given IDs.
   */
  async getProviderConfigurations(params?: GetProviderConfigurationsParams): Promise<ProviderConfigurationsResponse> {
    const url = `${this.baseUrl}/providers/configurations`;
    const { data } = await this.httpClient.get<ProviderConfigurationsResponse>(url, { params: params });
    return data;
  }

  /**
   * Retrieves normalized display info for a provider configuration.
   * Always returns the same shape across providers (`id`, `provider`, `name`, `channel`).
   * @param params - Query parameters with `provider` and `configId`.
   * @returns The display info for the given configuration.
   */
  async getProviderDisplayInfo(params: GetProviderDisplayInfoParams): Promise<ProviderDisplayInfoResponse> {
    const { data } = await this.httpClient.get<ProviderDisplayInfoResponse>(
      `${this.baseUrl}/providers/configs/display-info`,
      { params },
    );
    return data;
  }

  /**
   * Lists configurations for a provider (`GET /config/provider/:providerId`).
   * Scoped to the authenticated company's `companyId` from the token.
   * Each item includes the nested `providers` association.
   * @param providerId - Provider primary key (`Providers.id`, e.g. `1` for WhatsApp).
   * @param params - Optional filters on config columns and nested provider fields.
   * @returns `{ configurations }` for the given provider (empty array when none match).
   */
  async getConfigsByProvider(
    providerId: string | number,
    params?: GetConfigsByProviderParams,
  ): Promise<ConfigListByProviderResponse> {
    const { data } = await this.httpClient.get<ConfigListByProviderResponse>(
      `${this.baseUrl}/config/provider/${providerId}`,
      { params },
    );
    return data;
  }

  /**
   * Retrieves a queue by its ID.
   * @param queueId - The unique identifier of the queue.
   * @returns The queue data.
   */
  async getQueueById(queueId: string): Promise<Queue> {
    const { data } = await this.httpClient.get<Queue>(`${this.baseUrl}/queues/${queueId}`);
    return data;
  }

  async getQueues(): Promise<Queue[]> {
    const { data } = await this.httpClient.get<Queue[]>(`${this.baseUrl}/queues`);
    return data;
  }

  /**
   * Lists queue memberships for a member.
   * @param params - The member identifier and optional association filter.
   * @returns The queue memberships for the given member.
   */
  async getMemberQueues(params: GetMemberQueuesRequest): Promise<QueueMember[]> {
    const { data } = await this.httpClient.get<QueueMember[]>(`${this.baseUrl}/queue-members`, { params });
    return data;
  }

  /**
   * Retrieves service groups filtered by service group IDs.
   * @param params - Query parameters containing one or more service group IDs.
   * @returns The service groups matching the given IDs.
   */
  async getServiceGroups(params?: GetServiceGroupsParams): Promise<ServiceGroupsResponse> {
    const url = `${this.baseUrl}/service-groups`;
    const { data } = await this.httpClient.get<ServiceGroupsResponse>(url, { params: params });
    return data;
  }

  /**
   * Retrieves a Meta template configuration by its ID.
   * @param templateId - The unique identifier of the template.
   * @returns The Meta template configuration.
   */
  async getMetaTemplateById(templateId: string): Promise<MetaTemplateConfigResponse> {
    const { data } = await this.httpClient.get<MetaTemplateConfigResponse>(
      `${this.baseUrl}/meta-templates-config/${templateId}`,
    );
    return data;
  }

  /**
   * Retrieves template configurations associated with a configuration ID.
   * @param configId - The configuration identifier.
   * @returns The list of template configurations.
   */
  async getTemplateConfigsByConfigId(configId: string): Promise<MetaTemplateConfigsResponse> {
    const { data } = await this.httpClient.get<MetaTemplateConfigsResponse>(
      `${this.baseUrl}/meta-templates-config/config/${configId}`,
    );
    return data;
  }

  /**
   * Finds an existing queue rule or creates a new one if it does not exist.
   * @param payload - The payload containing companyId, queueId, and configId.
   * @returns The found or created queue rule.
   */
  async findOrCreateQueueRule(payload: FindOrCreateQueueRuleRequest): Promise<QueueRule> {
    const { data } = await this.httpClient.put<QueueRule>(`${this.baseUrl}/rules/queue/find-or-create`, payload);
    return data;
  }

  /**
   * Retrieves a rule by its ID (`GET /rules/:id`).
   * Pass `rootOnly: false` to expand the nested `flow` tree recursively.
   * @param ruleId - The unique identifier of the rule.
   * @param params - Optional query parameters (`rootOnly`, `includeFlow`, etc.).
   * @returns The rule, optionally with nested `flow`.
   */
  async getRuleById(ruleId: string, params?: GetRuleByIdRequest): Promise<RuleResponse> {
    const { data } = await this.httpClient.get<RuleResponse>(`${this.baseUrl}/rules/${ruleId}`, {
      params,
    });
    return data;
  }

  /**
   * @param params - The company and pagination parameters.
   * @returns The paginated initial service rules.
   */
  async getRootRules(params: GetRootRulesRequest): Promise<RootRulesResponse> {
    const { data } = await this.httpClient.get<RootRulesResponse>(`${this.baseUrl}/rules/paginated`, {
      params: {
        archiveStatus: 'unarchived',
        companyId: params.companyId,
        offset: params.offset,
        limit: params.limit,
        name: params?.name,
        isRoot: true,
      },
    });
    return data;
  }

  /**
   * Retrieves paginated rules with dynamic filters.
   * @param params - Query parameters for filtering and pagination.
   * @returns The paginated rules matching the given filters.
   */
  async getRules(params: GetRulesRequest): Promise<RulesResponse> {
    const { data } = await this.httpClient.get<RulesResponse>(`${this.baseUrl}/rules/paginated`, {
      params,
    });
    return data;
  }

  /**
   * Counts rules matching the given filters.
   * @param params - Query parameters for filtering rules (same filters as `getRules`, without pagination/associations).
   * @returns The total number of rules matching the filters.
   */
  async getRulesCount(params: GetRulesCountRequest): Promise<RulesCountResponse> {
    const { data } = await this.httpClient.get<RulesCountResponse>(`${this.baseUrl}/rules/count`, {
      params,
    });
    return data;
  }
}

export type {
  GetServiceGroupsParams,
  Queue,
  QueueMember,
  DistributionType,
  QueueListResponse,
  ServiceGroup,
  ServiceGroupsResponse,
} from './contracts/queues';
export type {
  GetProviderConfigurationsParams,
  GetProviderDisplayInfoParams,
  ProviderConfiguration,
  ProviderConfigurationsResponse,
  ProviderDisplayInfoResponse,
  ProviderListResponse,
  Provider,
  ProviderName,
  WhatsAppProviderSettings,
  WidgetProviderSettings,
} from './contracts/provider';
export type {
  ConfigListByProviderResponse,
  Config,
  ConfigProvider,
  GetConfigsByProviderParams,
} from './contracts/config';
export type {
  MetaTemplateButton,
  MetaTemplateConfigResponse,
  MetaTemplateConfigsResponse,
} from './contracts/meta-template';
export type {
  FindOrCreateQueueRuleRequest,
  GetRuleByIdRequest,
  GetRulesCountRequest,
  GetRulesRequest,
  QueueRule,
  RootRulesResponse,
  Rule,
  RuleAction,
  RuleArchiveStatus,
  RuleAssociation,
  RuleAutomaticMessage,
  RuleCallOnQueue,
  RuleFlowSettings,
  RuleFlowTextResponse,
  RuleHttpMethod,
  RuleInteractiveType,
  RuleQuestionnaire,
  RuleQuestionnaireBodyField,
  RuleQuestionnaireHeader,
  RuleQuestionnaireInteractiveOption,
  RuleQuestionnaireQuestion,
  RuleQuestionnaireQuestionType,
  RuleQuestionnaireValidationAction,
  RuleQuestionnaireValidationRegexType,
  RuleResponse,
  RuleSettings,
  RuleWebhook,
  RuleWebhookHeader,
  RuleWebhookResponseMapping,
  RulesCountResponse,
  RulesResponse,
} from './contracts/rule';
